import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Archive, UploadSimple, Eye, Trash, FileX,
  Scissors, Warning, Check, Info, Spinner
} from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { downloadWithAuth } from '@/lib/apiClient'; // eslint-disable-line no-unused-vars
import { LancamentosManuais } from './LancamentosManuais';
import { useNarrow, MobileRows, MobileRow, KV } from '@/components/historico/MobileRows';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtBRL = (centavos) => {
  const v = (centavos || 0) / 100;
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};
const fmtDateTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }); }
  catch { return iso; }
};

const HistoricoPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mode, setMode] = useState('lista'); // lista | importar | ver
  const [loteSelecionado, setLoteSelecionado] = useState(null);
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const isAdmin = (user?.role || '').toUpperCase() === 'ADMIN';

  const fetchLotes = async () => {
    setLoading(true); setErro('');
    try {
      const r = await fetch(`${API}/historico/lotes`);
      if (!r.ok) throw new Error('Falha ao carregar lotes');
      const d = await r.json();
      setLotes(d.lotes || []);
    } catch (e) { setErro(e.message || 'Erro'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (mode === 'lista') fetchLotes(); }, [mode]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#F7F6F4] flex items-center justify-center p-6">
        <div className="bg-white border border-[#E4E4E7] rounded-2xl p-8 max-w-md text-center">
          <Warning size={32} className="mx-auto text-[#DC2626] mb-3" />
          <h2 className="text-lg font-semibold text-[#18181B] mb-2">Acesso restrito</h2>
          <p className="text-sm text-[#71717A] mb-4">Esta area e exclusiva para administradores.</p>
          <button onClick={() => navigate('/gestao')} className="salonup-btn-back" data-testid="btn-voltar-gestao">
            <ArrowLeft size={16} /> Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="historico-page">
      <div className="salonup-container">
        <header className="salonup-header">
          <div className="salonup-brand">
            <div className="salonup-logo-mark"><Scissors size={22} weight="bold" /></div>
            <span className="salonup-logo-text">Salon<span>Up</span></span>
          </div>
          <button onClick={() => navigate('/gestao')} className="salonup-btn-back" data-testid="btn-voltar">
            <ArrowLeft size={18} weight="bold" /> <span>Voltar</span>
          </button>
        </header>

        <section className="mb-6">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h1 className="salonup-heading-lg">Historico</h1>
            <span className="salonup-badge" style={{ background: '#F3E8FF', color: '#6B21A8' }}>HISTORICO IMPORTADO</span>
          </div>
          <p className="salonup-text-body">
            Area somente leitura para dados convertidos de sistemas anteriores (Avec, etc). Nao afeta os modulos operacionais.
          </p>
        </section>

        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => { setMode('lista'); setLoteSelecionado(null); }}
            data-testid="tab-lista"
            className={`flex-1 sm:flex-none min-h-[44px] sm:min-h-0 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${mode === 'lista' ? 'bg-[#1B4332] text-white border-[#1B4332]' : 'bg-white text-[#71717A] border-[#E4E4E7]'}`}
          >
            <Archive size={16} className="inline mr-1" /> Lotes importados
          </button>
          <button
            onClick={() => { setMode('importar'); setLoteSelecionado(null); }}
            data-testid="tab-importar"
            className={`flex-1 sm:flex-none min-h-[44px] sm:min-h-0 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${mode === 'importar' ? 'bg-[#1B4332] text-white border-[#1B4332]' : 'bg-white text-[#71717A] border-[#E4E4E7]'}`}
          >
            <UploadSimple size={16} className="inline mr-1" /> Importar planilha
          </button>
          <button
            onClick={() => { setMode('manuais'); setLoteSelecionado(null); }}
            data-testid="tab-lancamentos-manuais"
            className={`flex-1 sm:flex-none min-h-[44px] sm:min-h-0 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${mode === 'manuais' ? 'bg-[#1B4332] text-white border-[#1B4332]' : 'bg-white text-[#71717A] border-[#E4E4E7]'}`}
          >
            <FileX size={16} className="inline mr-1" /> Lançamentos manuais
          </button>
        </div>

        {erro && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="erro-geral">
            {erro}
          </div>
        )}

        {mode === 'lista' && (
          <ListaLotes
            lotes={lotes}
            loading={loading}
            onVer={(lote) => { setLoteSelecionado(lote); setMode('ver'); }}
            onDesfeito={fetchLotes}
          />
        )}

        {mode === 'importar' && (
          <ImportarLote
            onImportado={() => { setMode('lista'); fetchLotes(); }}
          />
        )}

        {mode === 'manuais' && <LancamentosManuais />}

        {mode === 'ver' && loteSelecionado && (
          <VerLote
            loteId={loteSelecionado}
            onVoltar={() => { setLoteSelecionado(null); setMode('lista'); }}
          />
        )}
      </div>
    </div>
  );
};

// ==================== Lista ====================
const ListaLotes = ({ lotes, loading, onVer, onDesfeito }) => {
  const [confirmandoLote, setConfirmandoLote] = useState(null);
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');
  const [acao, setAcao] = useState(false);
  const narrow = useNarrow();

  const executar = async () => {
    if (justificativa.trim().length < 5) { setErro('Minimo 5 caracteres.'); return; }
    setAcao(true); setErro('');
    try {
      const r = await fetch(`${API}/historico/lotes/${encodeURIComponent(confirmandoLote.lote)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ justificativa: justificativa.trim() }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Falha ao desfazer'); }
      setConfirmandoLote(null); setJustificativa('');
      onDesfeito();
    } catch (e) { setErro(e.message); }
    finally { setAcao(false); }
  };

  if (loading) return <div className="text-center py-10 text-[#71717A]" data-testid="lotes-loading"><Spinner size={24} className="inline animate-spin mr-2" /> Carregando...</div>;
  if (!lotes.length) return (
    <div className="text-center py-16 bg-white rounded-2xl border border-[#E4E4E7]" data-testid="lotes-vazio">
      <Archive size={32} className="mx-auto text-[#A1A1AA] mb-3" />
      <p className="text-[#71717A]">Nenhum lote importado ate o momento.</p>
    </div>
  );

  return (
    <div className={narrow ? '' : 'bg-white rounded-2xl border border-[#E4E4E7] overflow-hidden'}>
      {narrow ? (
        <div className="space-y-2" data-testid="tabela-lotes">
          {lotes.map((l) => (
            <MobileRow key={l.id || l.lote} testId={`row-lote-${l.lote}`} title={l.lote} value={fmtBRL(l.totais?.total_pago_centavos)}
              meta={[['', `${fmtDate(l.periodo?.data_inicio)} – ${fmtDate(l.periodo?.data_fim)}`], ['Comandas', l.totais?.comandas_count || 0],
                ['Pagamentos', l.totais?.pagamentos_count || 0], ['Itens', l.totais?.itens_count || 0],
                ['Importado', `${fmtDateTime(l.importado_em)}${l.importado_por ? ` · ${l.importado_por.name || l.importado_por.login || ''}` : ''}`]]}>
              <div className="mt-1 border-t border-[#F4F4F5] pt-1">
                <KV label="Serviços" value={fmtBRL(l.totais?.total_servicos_centavos)} />
                <KV label="Produtos" value={fmtBRL(l.totais?.total_produtos_centavos)} />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button onClick={() => onVer(l.lote)} data-testid={`btn-ver-${l.lote}`} className="min-h-[40px] rounded-lg text-xs font-medium border border-[#E4E4E7] text-[#18181B]">
                  <Eye size={14} className="inline mr-1" /> Ver
                </button>
                <button onClick={() => setConfirmandoLote(l)} data-testid={`btn-desfazer-${l.lote}`} className="min-h-[40px] rounded-lg text-xs font-medium border border-red-200 text-red-700">
                  <Trash size={14} className="inline mr-1" /> Desfazer
                </button>
              </div>
            </MobileRow>
          ))}
        </div>
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="tabela-lotes">
          <thead className="bg-[#F7F6F4] text-[#71717A] text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Lote</th>
              <th className="text-left px-4 py-3">Periodo</th>
              <th className="text-right px-4 py-3">Pago</th>
              <th className="text-right px-4 py-3">Servicos</th>
              <th className="text-right px-4 py-3">Produtos</th>
              <th className="text-right px-4 py-3">Comandas</th>
              <th className="text-right px-4 py-3">Pagamentos</th>
              <th className="text-right px-4 py-3">Itens</th>
              <th className="text-left px-4 py-3">Importado em</th>
              <th className="text-left px-4 py-3">Por</th>
              <th className="text-right px-4 py-3">Acoes</th>
            </tr>
          </thead>
          <tbody>
            {lotes.map((l) => (
              <tr key={l.id || l.lote} className="border-t border-[#E4E4E7]" data-testid={`row-lote-${l.lote}`}>
                <td className="px-4 py-3 font-medium text-[#18181B]">{l.lote}</td>
                <td className="px-4 py-3 text-[#71717A]">{fmtDate(l.periodo?.data_inicio)} – {fmtDate(l.periodo?.data_fim)}</td>
                <td className="px-4 py-3 text-right">{fmtBRL(l.totais?.total_pago_centavos)}</td>
                <td className="px-4 py-3 text-right">{fmtBRL(l.totais?.total_servicos_centavos)}</td>
                <td className="px-4 py-3 text-right">{fmtBRL(l.totais?.total_produtos_centavos)}</td>
                <td className="px-4 py-3 text-right">{l.totais?.comandas_count || 0}</td>
                <td className="px-4 py-3 text-right">{l.totais?.pagamentos_count || 0}</td>
                <td className="px-4 py-3 text-right">{l.totais?.itens_count || 0}</td>
                <td className="px-4 py-3 text-[#71717A]">{fmtDateTime(l.importado_em)}</td>
                <td className="px-4 py-3 text-[#71717A]">{l.importado_por?.name || l.importado_por?.login || ''}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => onVer(l.lote)} data-testid={`btn-ver-${l.lote}`} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-[#E4E4E7] text-[#18181B] hover:bg-[#F7F6F4]">
                      <Eye size={14} className="inline mr-1" /> Ver
                    </button>
                    <button onClick={() => setConfirmandoLote(l)} data-testid={`btn-desfazer-${l.lote}`} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-700 hover:bg-red-50">
                      <Trash size={14} className="inline mr-1" /> Desfazer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {confirmandoLote && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="modal-desfazer">
          <div className="bg-white rounded-2xl max-w-md w-full p-4 sm:p-6">
            <h3 className="text-lg font-semibold mb-2 break-words">Desfazer lote {confirmandoLote.lote}</h3>
            <p className="text-sm text-[#71717A] mb-4">
              Serao removidas <b>{confirmandoLote.totais?.pagamentos_count}</b> linhas de pagamento e
              <b> {confirmandoLote.totais?.itens_count}</b> itens. Apenas as linhas deste lote. Nao afeta nenhum outro modulo.
            </p>
            <label className="text-xs font-semibold text-[#71717A] uppercase">Justificativa (minimo 5 caracteres)</label>
            <textarea
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              data-testid="input-justificativa-desfazer"
              className="w-full mt-1 mb-3 border border-[#E4E4E7] rounded-xl p-3 text-sm"
              rows={3}
            />
            {erro && <div className="mb-3 text-xs text-red-700">{erro}</div>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setConfirmandoLote(null); setJustificativa(''); setErro(''); }} className="px-4 py-2 rounded-xl text-sm border border-[#E4E4E7]" data-testid="btn-cancel-desfazer">Cancelar</button>
              <button onClick={executar} disabled={acao} className="px-4 py-2 rounded-xl text-sm bg-red-600 text-white disabled:opacity-50" data-testid="btn-confirmar-desfazer">
                {acao ? 'Desfazendo...' : 'Confirmar desfazer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== Importar ====================
const ImportarLote = ({ onImportado }) => {
  const inputRef = useRef(null);
  const [arquivo, setArquivo] = useState(null);
  const [preview, setPreview] = useState(null); // response do /preview
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const handleArquivo = (f) => {
    setArquivo(f || null); setPreview(null); setErro('');
  };

  const enviarPrevia = async () => {
    if (!arquivo) { setErro('Escolha um arquivo .xlsx'); return; }
    setEnviando(true); setErro(''); setPreview(null);
    try {
      const fd = new FormData(); fd.append('file', arquivo);
      const r = await fetch(`${API}/historico/preview`, { method: 'POST', body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Falha ao gerar previa');
      setPreview(d);
    } catch (e) { setErro(e.message); }
    finally { setEnviando(false); }
  };

  const confirmar = async () => {
    if (!preview?.preview_id) return;
    setConfirmando(true); setErro('');
    try {
      const r = await fetch(`${API}/historico/importar/${preview.preview_id}`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Falha ao importar');
      onImportado();
    } catch (e) { setErro(e.message); }
    finally { setConfirmando(false); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-[#E4E4E7] p-4 sm:p-6">
        <h3 className="font-semibold mb-3">1. Selecione a planilha padrao (.xlsx)</h3>
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            onChange={(e) => handleArquivo(e.target.files?.[0])}
            data-testid="input-arquivo-xlsx"
            className="text-sm w-full sm:w-auto min-w-0"
          />
          <button onClick={enviarPrevia} disabled={!arquivo || enviando}
            className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 rounded-xl text-sm bg-[#1B4332] text-white disabled:opacity-50"
            data-testid="btn-enviar-previa">
            {enviando ? <><Spinner size={14} className="inline animate-spin mr-1" /> Analisando...</> : 'Gerar previa'}
          </button>
        </div>
        {erro && <div className="mt-3 text-sm text-red-700" data-testid="erro-importar">{erro}</div>}
      </div>

      {preview && (
        <div className="bg-white rounded-2xl border border-[#E4E4E7] p-4 sm:p-6" data-testid="bloco-preview">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-semibold">2. Conferencia {preview.lote && <span className="text-[#71717A]">— lote <b>{preview.lote}</b></span>}</h3>
              {preview.periodo?.data_inicio && (
                <p className="text-xs text-[#71717A]">Periodo: {fmtDate(preview.periodo.data_inicio)} – {fmtDate(preview.periodo.data_fim)}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {preview.ok
                ? <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-green-50 text-green-700"><Check size={12} /> Pronto para confirmar</span>
                : <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-red-50 text-red-700"><FileX size={12} /> Bloqueado</span>}
            </div>
          </div>

          {preview.totais && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <KPI label="Total pago" value={fmtBRL(preview.totais.total_pago_centavos)} />
              <KPI label="Total itens" value={fmtBRL(preview.totais.total_itens_centavos)} />
              <KPI label="Servicos" value={fmtBRL(preview.totais.total_servicos_centavos)} />
              <KPI label="Produtos" value={fmtBRL(preview.totais.total_produtos_centavos)} />
              <KPI label="Comandas" value={preview.totais.comandas_count} sub={`${preview.comandas_fechadas} fechadas${preview.totais.comandas_sem_pagamento_count ? ` · inclui ${preview.totais.comandas_sem_pagamento_count} sem pagamento (R$ 0,00)` : ''}`} />
              <KPI label="Pagamentos" value={preview.totais.pagamentos_count} />
              <KPI label="Itens" value={preview.totais.itens_count} />
              <KPI label="Dias" value={preview.dias_fechados} sub="fechados" />
            </div>
          )}

          {preview.bloqueios?.length > 0 && (
            <div className="mb-4" data-testid="bloco-bloqueios">
              <div className="text-xs font-semibold text-red-700 uppercase mb-2">Bloqueios ({preview.bloqueios.length})</div>
              <ul className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-xl p-3 max-h-60 overflow-y-auto space-y-1 break-words">
                {preview.bloqueios.map((b, i) => <li key={i}>• {b}</li>)}
              </ul>
            </div>
          )}

          {preview.avisos?.length > 0 && (
            <div className="mb-4" data-testid="bloco-avisos">
              <div className="text-xs font-semibold text-amber-700 uppercase mb-2 flex items-center gap-1"><Info size={12} /> Avisos</div>
              <ul className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1 break-words">
                {preview.avisos.map((a, i) => <li key={i}>• {a}</li>)}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={confirmar}
              disabled={!preview.ok || confirmando}
              data-testid="btn-confirmar-importacao"
              className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-sm bg-[#1B4332] text-white disabled:opacity-50"
            >
              {confirmando ? 'Importando...' : 'Confirmar importacao'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const KPI = ({ label, value, sub }) => (
  <div className="rounded-xl border border-[#E4E4E7] p-3">
    <div className="text-[10px] uppercase text-[#71717A] tracking-wide">{label}</div>
    <div className="text-sm font-semibold text-[#18181B] mt-0.5 whitespace-nowrap">{value}</div>
    {sub && <div className="text-[10px] text-[#71717A] mt-0.5">{sub}</div>}
  </div>
);

// ==================== Ver lote ====================
const VerLote = ({ loteId, onVoltar }) => {
  const [data, setData] = useState(null);
  const [erro, setErro] = useState('');
  const [expand, setExpand] = useState({}); // comanda_num -> bool
  const narrow = useNarrow();

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/historico/lotes/${encodeURIComponent(loteId)}`);
        if (!r.ok) throw new Error('Falha ao carregar lote');
        setData(await r.json());
      } catch (e) { setErro(e.message); }
    })();
  }, [loteId]);

  const toggle = (c) => setExpand((p) => ({ ...p, [c]: !p[c] }));
  const info = data?.lote;

  if (erro) return <div className="p-4 text-red-700" data-testid="erro-ver">{erro}</div>;
  if (!data) return <div className="text-center py-10 text-[#71717A]"><Spinner size={24} className="inline animate-spin mr-2" /> Carregando lote...</div>;

  return (
    <div className="space-y-5" data-testid="ver-lote">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-semibold break-words">Lote {info.lote}</h2>
            <span className="salonup-badge" style={{ background: '#F3E8FF', color: '#6B21A8' }}>HISTORICO IMPORTADO</span>
          </div>
          <p className="text-xs text-[#71717A]">
            {fmtDate(info.periodo?.data_inicio)} – {fmtDate(info.periodo?.data_fim)} ·
            importado em {fmtDateTime(info.importado_em)} por {info.importado_por?.name || info.importado_por?.login}
          </p>
        </div>
        <button onClick={onVoltar} className="salonup-btn-back" data-testid="btn-voltar-lista"><ArrowLeft size={16} /> Voltar</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total pago" value={fmtBRL(data.totais.total_pago_centavos)} />
        <KPI label="Total itens" value={fmtBRL(data.totais.total_itens_centavos)} />
        <KPI label="Servicos" value={fmtBRL(data.totais.total_servicos_centavos)} />
        <KPI label="Produtos" value={fmtBRL(data.totais.total_produtos_centavos)} />
        <KPI label="Comandas" value={data.totais.comandas_count} sub={data.totais.comandas_sem_pagamento_count ? `+ ${data.totais.comandas_sem_pagamento_count} sem pagamento (R$ 0,00)` : undefined} />
        <KPI label="Pagamentos" value={data.totais.pagamentos_count} />
        <KPI label="Itens" value={data.totais.itens_count} />
        <KPI label="Dif." value={fmtBRL(data.totais.diferenca_centavos)} />
      </div>

      <div className="bg-white rounded-2xl border border-[#E4E4E7] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E4E4E7] font-semibold">Por dia</div>
        {narrow ? (
          <div className="p-2"><MobileRows testPrefix="ver-dia" empty="Sem dias." rows={data.por_dia.map(d => ({
            key: d.data, title: fmtDate(d.data), value: fmtBRL(d.pago_centavos),
            meta: [['Serviços', fmtBRL(d.servicos_centavos)], ['Produtos', fmtBRL(d.produtos_centavos)],
              ['Dif.', <span key="d" className={d.diferenca_centavos === 0 ? 'text-green-700' : 'text-red-700'}>{fmtBRL(d.diferenca_centavos)}</span>]],
          }))} /></div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F6F4] text-[#71717A] text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Data</th>
                <th className="text-right px-4 py-2">Pago</th>
                <th className="text-right px-4 py-2">Servicos</th>
                <th className="text-right px-4 py-2">Produtos</th>
                <th className="text-right px-4 py-2">Diferenca</th>
              </tr>
            </thead>
            <tbody>
              {data.por_dia.map((d) => (
                <tr key={d.data} className="border-t border-[#E4E4E7]">
                  <td className="px-4 py-2">{fmtDate(d.data)}</td>
                  <td className="px-4 py-2 text-right">{fmtBRL(d.pago_centavos)}</td>
                  <td className="px-4 py-2 text-right">{fmtBRL(d.servicos_centavos)}</td>
                  <td className="px-4 py-2 text-right">{fmtBRL(d.produtos_centavos)}</td>
                  <td className={`px-4 py-2 text-right ${d.diferenca_centavos === 0 ? 'text-green-700' : 'text-red-700'}`}>{fmtBRL(d.diferenca_centavos)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-[#E4E4E7] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E4E4E7] font-semibold">Por forma de pagamento</div>
          <table className="w-full text-sm">
            <thead className="bg-[#F7F6F4] text-[#71717A] text-xs uppercase tracking-wide">
              <tr><th className="text-left px-4 py-2">Forma</th><th className="text-right px-4 py-2">Qtd</th><th className="text-right px-4 py-2">Valor</th></tr>
            </thead>
            <tbody>
              {data.por_forma_pagamento.map((f) => (
                <tr key={f.forma_pagamento} className="border-t border-[#E4E4E7]">
                  <td className="px-3 sm:px-4 py-2 break-words">{f.forma_pagamento}</td>
                  <td className="px-3 sm:px-4 py-2 text-right">{f.qtd}</td>
                  <td className="px-3 sm:px-4 py-2 text-right whitespace-nowrap">{fmtBRL(f.valor_centavos)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-2xl border border-[#E4E4E7] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E4E4E7] font-semibold">Por profissional</div>
          {narrow ? (
            <div className="p-2"><MobileRows testPrefix="ver-prof" empty="Sem profissionais." rows={data.por_profissional.map(p => ({
              key: p.profissional, title: p.profissional, value: fmtBRL(p.total_centavos),
              meta: [['Serviços', fmtBRL(p.servicos_centavos)], ['Produtos', fmtBRL(p.produtos_centavos)]],
            }))} /></div>
          ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F7F6F4] text-[#71717A] text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Profissional</th>
                <th className="text-right px-4 py-2">Servicos</th>
                <th className="text-right px-4 py-2">Produtos</th>
                <th className="text-right px-4 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.por_profissional.map((p) => (
                <tr key={p.profissional} className="border-t border-[#E4E4E7]">
                  <td className="px-4 py-2">{p.profissional}</td>
                  <td className="px-4 py-2 text-right">{fmtBRL(p.servicos_centavos)}</td>
                  <td className="px-4 py-2 text-right">{fmtBRL(p.produtos_centavos)}</td>
                  <td className="px-4 py-2 text-right">{fmtBRL(p.total_centavos)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E4E4E7] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E4E4E7] font-semibold">Comandas ({data.totais.comandas_count}{data.totais.comandas_sem_pagamento_count ? ` + ${data.totais.comandas_sem_pagamento_count} sem pagamento` : ''})</div>
        {narrow ? (
          <div className="p-2 space-y-2">
            {data.comandas.map((c) => {
              const dif = (c.total_pagamentos_centavos || 0) - (c.total_itens_centavos || 0);
              const open = !!expand[c.comanda];
              return (
                <MobileRow key={c.comanda} testId={`row-comanda-${c.comanda}`} value={fmtBRL(c.total_pagamentos_centavos)}
                  title={<>#{c.comanda} {c.cliente_origem}{c.sem_pagamento && <span className="ml-1 whitespace-nowrap text-[9px] px-1 py-0.5 rounded uppercase tracking-wide bg-[#F4F4F5] text-[#71717A]" data-testid={`sem-pag-${c.comanda}`}>sem pagamento</span>}</>}
                  meta={[['', fmtDate(c.data_comanda)], ['Itens', fmtBRL(c.total_itens_centavos)],
                    ['Dif.', <span key="d" className={dif === 0 ? 'text-green-700' : 'text-red-700'}>{fmtBRL(dif)}</span>]]}>
                  <button onClick={() => toggle(c.comanda)} data-testid={`btn-toggle-${c.comanda}`} className="mt-1 min-h-[36px] text-xs text-[#1B4332] underline">
                    {open ? 'Fechar' : 'Expandir'}
                  </button>
                  {open && (
                    <div className="border-t border-[#F4F4F5] pt-1">
                      <div className="text-[10px] font-semibold text-[#71717A] uppercase mt-1">Itens</div>
                      {c.itens.map((it, idx) => <KV key={idx} label={`${it.tipo} · ${it.descricao_origem} · ${it.qtd}×`} value={fmtBRL(it.total_centavos)} />)}
                      <div className="text-[10px] font-semibold text-[#71717A] uppercase mt-2">Pagamentos</div>
                      {c.pagamentos.map((p, idx) => <KV key={idx} label={`#${p.seq_pagamento} ${p.forma_pagamento}${p.bandeira ? ` (${p.bandeira})` : ''} · ${fmtDate(p.data_pagamento)}`} value={fmtBRL(p.valor_centavos)} />)}
                    </div>
                  )}
                </MobileRow>
              );
            })}
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F6F4] text-[#71717A] text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">#</th>
                <th className="text-left px-4 py-2">Data</th>
                <th className="text-left px-4 py-2">Cliente (origem)</th>
                <th className="text-right px-4 py-2">Itens</th>
                <th className="text-right px-4 py-2">Pago</th>
                <th className="text-right px-4 py-2">Dif</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.comandas.map((c) => {
                const dif = (c.total_pagamentos_centavos || 0) - (c.total_itens_centavos || 0);
                const open = !!expand[c.comanda];
                return (
                  <>
                    <tr key={c.comanda} className="border-t border-[#E4E4E7]" data-testid={`row-comanda-${c.comanda}`}>
                      <td className="px-4 py-2 font-medium">
                        {c.comanda}
                        {c.sem_pagamento && <span className="ml-1 whitespace-nowrap text-[9px] px-1 py-0.5 rounded uppercase tracking-wide bg-[#F4F4F5] text-[#71717A]" data-testid={`sem-pag-${c.comanda}`}>sem pagamento</span>}
                      </td>
                      <td className="px-4 py-2 text-[#71717A]">{fmtDate(c.data_comanda)}</td>
                      <td className="px-4 py-2">{c.cliente_origem}</td>
                      <td className="px-4 py-2 text-right">{fmtBRL(c.total_itens_centavos)}</td>
                      <td className="px-4 py-2 text-right">{fmtBRL(c.total_pagamentos_centavos)}</td>
                      <td className={`px-4 py-2 text-right ${dif === 0 ? 'text-green-700' : 'text-red-700'}`}>{fmtBRL(dif)}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => toggle(c.comanda)} data-testid={`btn-toggle-${c.comanda}`} className="text-xs text-[#1B4332] underline">
                          {open ? 'Fechar' : 'Expandir'}
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-[#FAFAFA]">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <div className="text-xs font-semibold text-[#71717A] uppercase mb-1">Itens</div>
                              <table className="w-full text-xs">
                                <tbody>
                                  {c.itens.map((it, idx) => (
                                    <tr key={idx} className="border-b border-[#E4E4E7]">
                                      <td className="py-1">{it.tipo}</td>
                                      <td className="py-1">{it.descricao_origem}</td>
                                      <td className="py-1 text-right">{it.qtd}×</td>
                                      <td className="py-1 text-right">{fmtBRL(it.total_centavos)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-[#71717A] uppercase mb-1">Pagamentos</div>
                              <table className="w-full text-xs">
                                <tbody>
                                  {c.pagamentos.map((p, idx) => (
                                    <tr key={idx} className="border-b border-[#E4E4E7]">
                                      <td className="py-1">#{p.seq_pagamento}</td>
                                      <td className="py-1">{p.forma_pagamento}{p.bandeira ? ` (${p.bandeira})` : ''}</td>
                                      <td className="py-1 text-[#71717A]">{fmtDate(p.data_pagamento)}</td>
                                      <td className="py-1 text-right">{fmtBRL(p.valor_centavos)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
};

export default HistoricoPage;
export { HistoricoPage };
